/**
 * XRGestureHandler - WebXR gesture handler for AR mode
 * Handles touch input through XRSession select events
 */

interface TouchState {
  inputSource: XRInputSource
  startTime: number
  startPosition: { x: number; y: number; z: number } | null
  currentPosition: { x: number; y: number; z: number }
}

export class XRGestureHandler {
  private session: XRSession | null = null
  private renderer: any = null // splatv-module instance
  
  // Track active touches by input source
  private activeTouches = new Map<XRInputSource, TouchState>()
  
  // Gesture state
  private gestureType: 'unknown' | 'tap' | 'pan' | 'pinch' | 'rotate' = 'unknown'
  
  // Model transform
  private modelPosition = { x: 0, y: 0, z: -2 }
  private modelRotation = { x: 0, y: 0, z: 0 }
  private modelScale = 1.0
  
  // Gesture tracking for multi-touch
  private lastPinchDistance = 0
  private lastRotationAngle = 0
  
  constructor(renderer: any) {
    this.renderer = renderer
    
    // Bind event handlers
    this.onSelectStart = this.onSelectStart.bind(this)
    this.onSelectEnd = this.onSelectEnd.bind(this)
    this.onSelect = this.onSelect.bind(this)
  }
  
  /**
   * Initialize gesture handling for XR session
   */
  initialize(session: XRSession) {
    this.session = session
    
    // Register session event listeners
    session.addEventListener('selectstart', this.onSelectStart)
    session.addEventListener('selectend', this.onSelectEnd)
    session.addEventListener('select', this.onSelect)
    
    // Also listen for input source changes to debug
    session.addEventListener('inputsourceschange', (event: any) => {
      console.log('Input sources changed:')
      console.log('  Added:', event.added.map((s: XRInputSource) => 
        `${s.targetRayMode} (${s.handedness})`))
      console.log('  Removed:', event.removed.map((s: XRInputSource) => 
        `${s.targetRayMode} (${s.handedness})`))
      console.log('  Current sources:', session.inputSources.length)
    })
    
    console.log('XRGestureHandler initialized')
    console.log('Initial input sources:', session.inputSources.length)
    for (let i = 0; i < session.inputSources.length; i++) {
      const source = session.inputSources[i]
      console.log(`  Source ${i}: ${source.targetRayMode} (${source.handedness})`)
    }
  }
  
  /**
   * Handle select start (touch down)
   */
  private onSelectStart(event: XRInputSourceEvent) {
    const inputSource = event.inputSource
    
    // Log all input sources for debugging
    console.log(`SelectStart - targetRayMode: ${inputSource.targetRayMode}, handedness: ${inputSource.handedness}`)
    
    // Handle both transient-pointer and screen touch inputs
    // Android Chrome may use 'screen' instead of 'transient-pointer'
    if (inputSource.targetRayMode !== 'transient-pointer' && inputSource.targetRayMode !== 'screen') {
      console.log(`Ignoring input source with targetRayMode: ${inputSource.targetRayMode}`)
      return
    }
    
    // Create touch state for this input source
    const touchState: TouchState = {
      inputSource,
      startTime: performance.now() / 1000,
      startPosition: null,
      currentPosition: { x: 0, y: 0, z: 0 }
    }
    
    this.activeTouches.set(inputSource, touchState)
    
    console.log(`Touch started. Active touches: ${this.activeTouches.size}`)
    
    // Reset gesture type when starting new gesture
    if (this.activeTouches.size === 1) {
      this.gestureType = 'unknown'
    } else if (this.activeTouches.size === 2) {
      // Starting multi-touch gesture
      this.gestureType = 'unknown'
      this.lastPinchDistance = 0
      this.lastRotationAngle = 0
    }
  }
  
  /**
   * Handle select event (for single tap)
   */
  private onSelect(event: XRInputSourceEvent) {
    const inputSource = event.inputSource
    console.log(`Select - targetRayMode: ${inputSource.targetRayMode}`)
    
    // Log select event but don't process it if we're already handling selectstart/end
    if (this.activeTouches.size > 0) {
      console.log('Select event during active touch, ignoring')
    }
  }
  
  /**
   * Handle select end (touch up)
   */
  private onSelectEnd(event: XRInputSourceEvent) {
    const inputSource = event.inputSource
    
    console.log(`SelectEnd - targetRayMode: ${inputSource.targetRayMode}`)
    
    // Handle both transient-pointer and screen touch inputs
    if (inputSource.targetRayMode !== 'transient-pointer' && inputSource.targetRayMode !== 'screen') {
      return
    }
    
    const touchState = this.activeTouches.get(inputSource)
    if (!touchState) {
      console.warn('Select end for unknown input source')
      return
    }
    
    // Check for tap gesture
    const elapsed = performance.now() / 1000 - touchState.startTime
    const moved = touchState.startPosition ? 
      this.calculateDistance(touchState.startPosition, touchState.currentPosition) : 0
    
    if (elapsed < 0.3 && moved < 0.01 && this.activeTouches.size === 1) {
      this.handleTap()
    }
    
    // Remove this touch
    this.activeTouches.delete(inputSource)
    
    console.log(`Touch ended. Active touches: ${this.activeTouches.size}`)
    
    // Reset gesture type when all touches end
    if (this.activeTouches.size === 0) {
      this.gestureType = 'unknown'
    }
  }
  
  /**
   * Update gesture processing (call each frame)
   */
  update(frame: XRFrame, referenceSpace: XRReferenceSpace) {
    if (!this.session) return
    
    // Update positions for all active touches
    for (const [inputSource, touchState] of this.activeTouches) {
      if (inputSource.targetRaySpace) {
        const pose = frame.getPose(inputSource.targetRaySpace, referenceSpace)
        if (pose) {
          const position = pose.transform.position
          touchState.currentPosition = { x: position.x, y: position.y, z: position.z }
          
          // Set start position after initial stabilization
          if (!touchState.startPosition && 
              performance.now() / 1000 - touchState.startTime > 0.05) {
            touchState.startPosition = { ...touchState.currentPosition }
            console.log('Start position set:', touchState.startPosition)
          }
        }
      }
    }
    
    // Process gestures based on number of active touches
    const touchCount = this.activeTouches.size
    
    if (touchCount === 1) {
      this.processSingleTouch()
    } else if (touchCount === 2) {
      this.processMultiTouch()
    }
  }
  
  /**
   * Process single touch gestures (pan)
   */
  private processSingleTouch() {
    const touchState = Array.from(this.activeTouches.values())[0]
    if (!touchState || !touchState.startPosition) return
    
    const delta = {
      x: touchState.currentPosition.x - touchState.startPosition.x,
      y: touchState.currentPosition.y - touchState.startPosition.y,
      z: touchState.currentPosition.z - touchState.startPosition.z
    }
    
    const distance = this.calculateDistance(touchState.startPosition, touchState.currentPosition)
    
    // Detect gesture type
    if (this.gestureType === 'unknown' && distance > 0.01) {
      this.gestureType = 'pan'
      console.log('Gesture detected: pan')
    }
    
    // Apply pan
    if (this.gestureType === 'pan') {
      // Use delta for smoother movement
      // Increased sensitivity significantly because AR touch deltas are very small (0.0001-0.001 range)
      const sensitivity = 50.0
      this.modelPosition.x += delta.x * sensitivity
      this.modelPosition.y += delta.y * sensitivity
      this.modelPosition.z += delta.z * sensitivity
      
      // Log actual position changes for debugging
      console.log(`Pan delta: x=${delta.x.toFixed(4)}, y=${delta.y.toFixed(4)}, z=${delta.z.toFixed(4)}`)
      console.log(`New position: x=${this.modelPosition.x.toFixed(4)}, y=${this.modelPosition.y.toFixed(4)}, z=${this.modelPosition.z.toFixed(4)}`)
      
      // Update start position for continuous panning
      touchState.startPosition = { ...touchState.currentPosition }
      
      this.updateModelTransform()
    }
  }
  
  /**
   * Process multi-touch gestures (pinch/rotate)
   */
  private processMultiTouch() {
    const touches = Array.from(this.activeTouches.values())
    if (touches.length !== 2) return
    
    const [touch1, touch2] = touches
    if (!touch1.startPosition || !touch2.startPosition) return
    
    const pos1 = touch1.currentPosition
    const pos2 = touch2.currentPosition
    
    // Calculate distance for pinch
    const distance = this.calculateDistance(pos1, pos2)
    
    // Calculate angle for rotation (in XZ plane for AR)
    const angle = Math.atan2(pos2.z - pos1.z, pos2.x - pos1.x)
    
    // Detect gesture type
    if (this.gestureType === 'unknown') {
      // Determine if pinch or rotate based on initial movement
      this.gestureType = 'pinch' // Default to pinch for now
      console.log('Gesture detected: pinch/rotate')
    }
    
    // Apply pinch (scale)
    if (this.lastPinchDistance > 0) {
      const scaleDelta = distance / this.lastPinchDistance
      this.modelScale *= scaleDelta
      this.modelScale = Math.max(0.1, Math.min(5.0, this.modelScale))
    }
    this.lastPinchDistance = distance
    
    // Apply rotation around Y axis
    if (this.lastRotationAngle !== 0) {
      const angleDelta = angle - this.lastRotationAngle
      // Limit rotation speed
      const maxRotation = Math.PI / 8 // Max 22.5 degrees per frame
      const clampedDelta = Math.max(-maxRotation, Math.min(maxRotation, angleDelta))
      this.modelRotation.y += clampedDelta
    }
    this.lastRotationAngle = angle
    
    this.updateModelTransform()
  }
  
  /**
   * Calculate distance between two positions
   */
  private calculateDistance(pos1: { x: number; y: number; z: number }, 
                           pos2: { x: number; y: number; z: number }): number {
    const dx = pos2.x - pos1.x
    const dy = pos2.y - pos1.y
    const dz = pos2.z - pos1.z
    return Math.sqrt(dx * dx + dy * dy + dz * dz)
  }
  
  /**
   * Handle tap gesture
   */
  private handleTap() {
    console.log('Tap detected')
    // Could implement double-tap to reset transform
    // For now, just log
  }
  
  /**
   * Update model transform in renderer
   */
  private updateModelTransform() {
    const transform = {
      position: this.modelPosition,
      rotation: {
        rx: this.modelRotation.x,
        ry: this.modelRotation.y,
        rz: this.modelRotation.z
      },
      scale: { x: this.modelScale, y: this.modelScale, z: this.modelScale }
    }
    
    console.log('Updating model transform:', {
      position: `(${transform.position.x.toFixed(3)}, ${transform.position.y.toFixed(3)}, ${transform.position.z.toFixed(3)})`,
      rotation: `(${transform.rotation.rx.toFixed(2)}, ${transform.rotation.ry.toFixed(2)}, ${transform.rotation.rz.toFixed(2)})`,
      scale: transform.scale.x.toFixed(2)
    })
    
    if (this.renderer && this.renderer.setModelTransform) {
      this.renderer.setModelTransform(transform)
    }
  }
  
  /**
   * Get current model transform
   */
  getModelTransform() {
    return {
      position: { ...this.modelPosition },
      rotation: {
        rx: this.modelRotation.x,
        ry: this.modelRotation.y,
        rz: this.modelRotation.z
      },
      scale: { x: this.modelScale, y: this.modelScale, z: this.modelScale }
    }
  }
  
  /**
   * Reset model transform to default
   */
  reset() {
    this.modelPosition = { x: 0, y: 0, z: -2 }
    this.modelRotation = { x: 0, y: 0, z: 0 }
    this.modelScale = 1.0
    this.updateModelTransform()
  }
  
  /**
   * Clean up
   */
  dispose() {
    if (this.session) {
      this.session.removeEventListener('selectstart', this.onSelectStart)
      this.session.removeEventListener('selectend', this.onSelectEnd)
    }
    
    this.activeTouches.clear()
    this.session = null
    this.renderer = null
  }
}